import { ApexOptions } from "apexcharts";
import { t } from "i18next";
import { Theme } from "mui-styles";
import { getEnvVariables } from "src/api/api";
import { Deployments } from "src/types/deployments";
import { User } from "src/types/user";
import { getColor } from "./colorhelpers";
import { getNsDataFromCollection } from "./nsapihelpers";
import { GlucObject, GlucRanges } from "./typehelpers";

export interface View {
  name: string;
  interval: number;
  isActive: boolean;
  start: number;
  type: "days" | "weeks" | "months";
}

export const views: Record<string, Record<number, View>> = {
  days: {
    1: {
      name: "1 Day",
      interval: 288,
      isActive: false,
      start: 0,
      type: "days",
    },
    2: {
      name: "2 Days",
      interval: 288 * 2,
      isActive: false,
      start: 0,
      type: "days",
    },
    3: {
      name: "3 Days",
      interval: 288 * 3,
      isActive: false,
      start: 0,
      type: "days",
    },
  },
  weeks: {
    1: {
      name: "1 Week",
      interval: 288 * 7,
      isActive: false,
      start: 0,
      type: "weeks",
    },
    2: {
      name: "2 Weeks",
      interval: 288 * 7 * 2,
      isActive: false,
      start: 0,
      type: "weeks",
    },
    3: {
      name: "3 Weeks",
      interval: 288 * 7 * 3,
      isActive: false,
      start: 0,
      type: "weeks",
    },
  },
  months: {
    1: {
      name: "Month",
      interval: 288 * 31,
      isActive: false,
      start: 0,
      type: "months",
    },
    2: {
      name: "3 Months",
      interval: 288 * 31 * 3,
      isActive: false,
      start: 0,
      type: "months",
    },
    3: {
      name: "6 Months",
      interval: 288 * 31 * 6,
      isActive: false,
      start: 0,
      type: "months",
    },
  },
};

export const getGlucoseData = async (
  user: User,
  period: { start: Date; end: Date }
) => {
  if (!user?.nsInstance?.validated || !user?.roles?.includes("nightscout")) {
    console.error(t("UserNSInstanceNotFound"));
    return [];
  }
  try {
    // Format endpoint
    const endpoint = `entries`;
    const variables = {
      count: 100000,
      start: period.start.getTime(),
      end: period.end.getTime(),
    };
    const { data } = await getNsDataFromCollection(user, endpoint, variables);
    const { envVars } = await getEnvVariables(user, Deployments.NIGHTSCOUT);
    if (!envVars) {
      return [];
    }
    const DISPLAY_UNITS = envVars.DISPLAY_UNITS;
    const multiplier =
      DISPLAY_UNITS === "mmol/L" || DISPLAY_UNITS === "mmol" ? 0.0555 : 1;

    const glucoseSeries: GlucObject[] = data
      .filter((glucObj: any) => glucObj.sgv !== undefined)
      .map((glucObj: any) => {
        return {
          id: glucObj.date.toString(),
          label: "glucose",
          date: glucObj.date,
          value: Number((glucObj.sgv * multiplier).toFixed(1)),
        };
      });

    return glucoseSeries;
  } catch (error) {
    console.error("Error fetching:", user.email, error);
    return [];
  }
};

export const getGlucoseDataCoaching = async (
  user: User,
  period: { start: Date; end: Date }
) => {
  if (
    !user ||
    !user?.nsInstance?.url ||
    !user?.nsInstance?.id ||
    !user?.roles?.includes("nightscout")
  )
    return [];
  try {
    // Format endpoint
    const endpoint = `entries`;
    const variables = {
      count: 100000,
      start: period.start.getTime(),
      end: period.end.getTime(),
    };
    // Get env variables for access token
    const { data } = await getNsDataFromCollection(user, endpoint, variables);

    const glucoseSeries: GlucObject[] = data.map((glucObj: any) => {
      return {
        id: glucObj.date.toString(),
        label: "glucose",
        date: glucObj.date,
        value: Number(glucObj.sgv.toFixed(1)),
      };
    });

    return glucoseSeries;
  } catch (error) {
    console.error("Error fetching:", user.email, error);
    return [];
  }
};

export const findMedian = (arr: number[]): number => {
  // Sort the array in ascending order
  arr.sort(function (a, b) {
    return a - b;
  });

  const length = arr.length;
  const middle = Math.floor(length / 2);

  // Check if the array has an even number of elements
  if (length % 2 === 0) {
    // If even, return the average of the two middle values
    return (arr[middle - 1] + arr[middle]) / 2;
  } else {
    // If odd, return the middle value
    return arr[middle];
  }
};

const oneDayInseconds = 1000 * 60 * 60 * 24;
const oneWeekInseconds = oneDayInseconds * 7;
const oneMonthInseconds = oneDayInseconds * 30;

export const moveGraph: Record<string, Record<string, { time: number }>> = {
  day: {
    forward: { time: oneDayInseconds },
    backward: { time: -oneDayInseconds },
  },
  week: {
    forward: { time: oneWeekInseconds },
    backward: { time: -oneWeekInseconds },
  },
  days: {
    forward: { time: oneMonthInseconds },
    backward: { time: -oneMonthInseconds },
  },
};

export type ChartSeries = {
  name?: string;
  data: Array<number>[];
}[];

export const secondsToTime = (seconds: number, returnStyle: "h" | "h+m") => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  // Add leading zeros if needed
  const hoursFormatted = hours < 10 ? "0" + hours : hours;
  const minutesFormatted = minutes < 10 ? "0" + minutes : minutes;
  const remainingSecondsFormatted =
    remainingSeconds < 10 ? "0" + remainingSeconds : remainingSeconds;

  return (
    ((returnStyle === "h+m" &&
      hoursFormatted + ":" + minutesFormatted) as string) ||
    ((returnStyle === "h" && hoursFormatted) as string)
  );
};

export const getCleanProfileDetail = (basals: [number, number][]) => {
  // get row of time of every half hour of a day
  const timeRow = [];
  for (let i = 0; i < 48; i++) {
    timeRow.push(i * 1800);
  }

  // create empty array
  const cleanedBasals: Record<number, number> = {};

  // get latest basal of the day
  let lastBasal = [...basals].reverse()[0][1];

  // loop over each half hour and add basal if new, otherwise used last basal
  timeRow.forEach((time) => {
    basals.forEach((basal) => {
      if (cleanedBasals[time]) return;
      if (basal[0] === time) {
        cleanedBasals[time] = basal[1];
        lastBasal = basal[1];
      }
    });
    cleanedBasals[time] = lastBasal;
  });

  const basalsNew = Object.entries(cleanedBasals).map(([key, value]) => {
    return [parseInt(key), value];
  });

  return basalsNew;
};

export const groupBy = <T, K extends string | number | symbol>(
  arr: T[],
  key: (item: T) => K
) => {
  return arr.reduce((acc, el) => {
    const group = key(el);
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(el);
    return acc;
  }, {} as Record<K, T[]>);
};

export const getEndOfDay = (date: Date | number) => {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay;
};

export const zipArrays = <T, D>(arr1: Array<T>, arr2: Array<D>) => {
  // Assuming arr1 and arr2 have the same length

  if (arr1.length !== arr2.length) {
    throw new Error("Arrays must have the same length");
  }
  return arr1.map((element, index) => [element, arr2[index]]);
};

export const distributionNames = ["InRange", "Hypo", "Hyper"];

export const getMinMax = (glucoseSeries: GlucObject[]) => {
  const min = new Date(Math.min(...glucoseSeries.map((i) => i.date)));
  const max = new Date(Math.max(...glucoseSeries.map((i) => i.date)));
  return [min, max];
};

export const getChartOptions = (
  range: GlucRanges,
  theme: Theme
): ApexOptions => {
  return {
    chart: {
      id: range,
      group: "social",
      type: "line",
      height: 160,
      width: 300,
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: true,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
    },
    dataLabels: {
      enabled: false,
    },
    colors: [getColor(range as GlucRanges, theme)],
  };
};

export const getPercentiles = (glucSeries: GlucObject[]) => {
  if (!glucSeries || glucSeries.length === 0) {
    return { min: 0, max: 0, Q1: 0, Q3: 0, median: 0 };
  }
  const min = Math.min(...glucSeries.map((i) => i.value));
  const max = Math.max(...glucSeries.map((i) => i.value));
  const values = glucSeries.map((i) => Number(i.value));
  const sortedValues = values.sort((a, b) => a - b);
  const median = findMedian(sortedValues);
  const Q1 = findMedian(sortedValues.slice(0, sortedValues.length / 2));
  const Q3 = findMedian(sortedValues.slice(sortedValues.length / 2));
  const Q12 = findMedian(sortedValues.slice(0, sortedValues.length / 4));
  const Q88 = findMedian(sortedValues.slice((sortedValues.length / 4) * 3));

  return { min, max, Q1, Q3, Q12, Q88, median };
};

export const getAverage = (numbers: number[]): number => {
  if (numbers.length === 0) return 0;

  const sum = numbers.reduce((acc, num) => acc + num, 0);
  return sum / numbers.length;
};

export const calculateOutOfRangeTransitions = (
  glucSeries: GlucObject[],
  user: User
): { lowTransitions: number; highTransitions: number } => {
  if (glucSeries.length < 2) return { lowTransitions: 0, highTransitions: 0 };

  let lowTransitions = 0;
  let highTransitions = 0;
  const sortedSeries = [...glucSeries].sort((a, b) => a.date - b.date);

  for (let i = 1; i < sortedSeries.length; i++) {
    const prevReading = Number(sortedSeries[i - 1].value);
    const currentReading = Number(sortedSeries[i].value);

    // Check if previous reading was in range
    const wasInRange =
      prevReading >= user.targetLow && prevReading <= user.targetHigh;

    // Check transitions to low and high
    const isNowLow = currentReading < user.targetLow;
    const isNowHigh = currentReading > user.targetHigh;

    if (wasInRange && isNowLow) {
      lowTransitions++;
    }
    if (wasInRange && isNowHigh) {
      highTransitions++;
    }
  }

  return { lowTransitions, highTransitions };
};
